import React, { useState, useEffect, useRef } from 'react';
import PacientSearchSelect from '../../PacientSearchSelect/PacientSearchSelect';
import { fetchProcedimentos, createBooking, fetchBookings, fetchPatients } from '../../api';
import { ClipLoader } from 'react-spinners';
import { useLocation } from 'react-router-dom';
import { FaCalendarAlt, FaClipboardList, FaLock, FaNotesMedical, FaPlus, FaRegCalendarCheck, FaUser } from 'react-icons/fa';
import { parseCurrencyValue } from '../../../utils/currency';

function CreateBooking({ professionals, rooms }) {
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState('30');
  const [selectedHour, setSelectedHour] = useState('');
  const [selectedMinute, setSelectedMinute] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [procedures, setProcedures] = useState([]);
  const [bookingAgents, setBookingAgents] = useState([]);
  const [selectedBookingAgent, setSelectedBookingAgent] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [otherChannelText, setOtherChannelText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProcedures, setSelectedProcedures] = useState([]);
  const [showSubProcedures, setShowSubProcedures] = useState(false);
  const [currentProcedure, setCurrentProcedure] = useState(null);
  const [generalObservation, setGeneralObservation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showMissingFieldsModal, setShowMissingFieldsModal] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [patientLastBookings, setPatientLastBookings] = useState([]);
  const [patientBookingsLoading, setPatientBookingsLoading] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomBookings, setRoomBookings] = useState([]);
  const [roomBookingsLoading, setRoomBookingsLoading] = useState(false);
  const location = useLocation();
  
  // Ref for patient search component
  const patientSearchRef = useRef(null);
  const dateInputRef = useRef(null);

  useEffect(() => {
    fetchProcedimentos()
      .then(data => {
        const proceduresList = Object.entries(data.config.procedimentos).map(([key, value]) => ({
          name: key,
          ...value  
        }));
        setProcedures(proceduresList);

        const agentsMap = (data.config && data.config.booking_agents) || {};
        const agentsList = Object.entries(agentsMap).map(([name, info]) => ({
          id: info.id,
          name
        }));
        setBookingAgents(agentsList);
      })
      .catch(error => console.error('Error fetching procedures:', error));
  }, []);

  useEffect(() => {
    const state = location.state && location.state.prefillFromBooking;
    if (!state) return;

    if (state.professionalId) {
      const prof = professionals.find(p => p.id === state.professionalId);
      if (prof) setSelectedProfessional(prof);
    }

    if (state.patient) {
      setSelectedPatient(state.patient);
      if (state.patient.pacientId) {
        fetchPatientLastBookings(state.patient.pacientId);
      } else if (state.patient.Nome) {
        (async () => {
          try {
            const results = await fetchPatients(state.patient.Nome);
            const exact = Array.isArray(results) ? results.find(p => p.Nome === state.patient.Nome) : null;
            const resolved = exact || (Array.isArray(results) && results.length > 0 ? results[0] : null);
            if (resolved) {
              setSelectedPatient(resolved);
              if (resolved.pacientId) fetchPatientLastBookings(resolved.pacientId);
            }
          } catch (e) {
          }
        })();
      }
    }

    if (Array.isArray(state.selectedProcedures)) {
      setSelectedProcedures(state.selectedProcedures);
    }

    if (state.duration) {
      setSelectedDuration(String(state.duration));
    }

    if (state.observacao) {
      setGeneralObservation(state.observacao);
    }

    if (state.roomId) {
      const room = rooms.find(r => r.id === state.roomId);
      if (room) setSelectedRoom(room);
    }

    if (state.booking_agent_name) {
      const agent = bookingAgents.find(a => a.name === state.booking_agent_name);
      if (agent) setSelectedBookingAgent(agent);
    }

    if (state.booking_channel) {
      if (state.booking_channel.startsWith('Outro: ')) {
        setSelectedChannel('Outro');
        setOtherChannelText(state.booking_channel.replace('Outro: ', ''));
      } else {
        setSelectedChannel(state.booking_channel);
        setOtherChannelText('');
      }
    }
  }, [location.state, professionals, rooms, bookingAgents]);

  // Load room bookings when a room is selected or date changes
  useEffect(() => {
    if (selectedRoom?.id && selectedDate && selectedProfessional?.id) {
      loadRoomBookings(selectedRoom.id, selectedDate, selectedProfessional.id);
    } else {
      setRoomBookings([]);
    }
  }, [selectedRoom, selectedDate, selectedProfessional]);

  const loadRoomBookings = async (roomId, date, professionalId) => {
    try {
      setRoomBookingsLoading(true);
      
      const queryParams = {
        room_id: roomId,
        date: date,
        specified_index: "room"
      };
      
      const bookingsData = await fetchBookings(queryParams);
      
      const professionalQueryParams = {
        professionalId: professionalId,
        date: date
      };
      
      const professionalBlockings = await fetchBookings(professionalQueryParams);
      
      const blockingEntries = professionalBlockings.filter(booking => booking.status === 'Bloqueio');
      
      const allBookings = [...bookingsData, ...blockingEntries];
      
      const uniqueBookings = [];
      const bookingIds = new Set();
      
      allBookings
        .filter(booking => booking.status !== 'Cancelado')
        .forEach(booking => {
          if (booking.atendimento_id && !bookingIds.has(booking.atendimento_id)) {
            bookingIds.add(booking.atendimento_id);
            uniqueBookings.push(booking);
          } else if (!booking.atendimento_id) {
            const bookingKey = `${booking.datetimeAtendimento}-${booking.paciente || 'Bloqueio'}-${booking.status}`;
            if (!bookingIds.has(bookingKey)) {
              bookingIds.add(bookingKey);
              uniqueBookings.push(booking);
            }
          }
        });
      
      setRoomBookings(uniqueBookings);
    } catch (error) {
      console.error('Error fetching room bookings:', error);
    } finally {
      setRoomBookingsLoading(false);
    }
  };

  const handlePatientSelect = (patient) => {
    // console.log(patient);
    setSelectedPatient(patient);
    
    // Fetch last 3 bookings for this patient
    if (patient && patient.pacientId) {
      fetchPatientLastBookings(patient.pacientId);
    }
  };

  // Function to fetch patient's last bookings
  const fetchPatientLastBookings = async (patientId) => {
    try {
      setPatientBookingsLoading(true);
      // Get today's date
      const today = new Date().toISOString().split('T')[0];
      
      // Create query params
      const queryParams = {
        patientId: patientId,
        endDate: today
      };
      
      // Fetch bookings
      const bookingsData = await fetchBookings(queryParams);
      
      // Filter out cancelled bookings and get the last 3
      const filteredBookings = bookingsData
        .filter(booking => booking.status !== 'Cancelado')
        .sort((a, b) => new Date(b.datetimeAtendimento) - new Date(a.datetimeAtendimento))
        .slice(0, 3);
      
      setPatientLastBookings(filteredBookings);
    } catch (error) {
      console.error('Error fetching patient bookings:', error);
    } finally {
      setPatientBookingsLoading(false);
    }
  };

  const handleProcedureSelect = (procedure) => {
    if (procedure.subcategorias) {
      setCurrentProcedure(procedure);
      setShowSubProcedures(true);
    } else {
      addProcedure(procedure, null, { valor: procedure.valor });
    }
  };

  const handleSubProcedureSelect = (subProcedureName, subProcedure) => {
    addProcedure(currentProcedure, subProcedureName, subProcedure);
    setShowSubProcedures(false);
    setCurrentProcedure(null);
  };

  const addProcedure = (procedure, subProcedureName, subProcedure) => {
    const procedureName = typeof procedure === 'string' ? procedure : procedure.name;
    
    const value = subProcedure.valor || 0;
    
    let commission = null;
    if (subProcedure && 'comission' in subProcedure) {
      commission = subProcedure.comission;
    } else if (procedure && 'comission' in procedure) {
      commission = procedure.comission;
    }
    
    const newProcedure = {
      procedure: procedureName,
      subProcedure: subProcedureName,
      value: value,
      commission: commission
    };
    
    // console.log('New procedure:', newProcedure);
    setSelectedProcedures([...selectedProcedures, newProcedure]);
  };

  const updateProcedure = (index, field, value) => {
    const updatedProcedures = [...selectedProcedures];
    updatedProcedures[index] = {
      ...updatedProcedures[index],
      [field]: value
    };
    setSelectedProcedures(updatedProcedures);
  };

  const removeProcedure = (index) => {
    const updatedProcedures = selectedProcedures.filter((_, i) => i !== index);
    setSelectedProcedures(updatedProcedures);
  };

  const renderSelectedProcedures = () => {
    return selectedProcedures.map((proc, index) => (
      <div key={index} className="selected-procedure-item">
        <div className="procedure-details">
          <div className="procedure-name">
            <span>{proc.procedure}</span>
            {proc.subProcedure && <span className="subprocedure-name"> - {proc.subProcedure}</span>}
          </div>
          
          <div className="procedure-value-edit">
            <label>Valor R$:</label>
            <input 
              type="text"
              inputMode="decimal"
              value={proc.value} 
              onChange={(e) => {
                updateProcedure(index, 'value', e.target.value);
              }}
            />
            <div className="procedure-actions">
              <button 
                className="discount-button"
                onClick={(e) => {
                  e.stopPropagation();
                  const discountOverlay = document.getElementById(`discount-overlay-${index}`);
                  if (discountOverlay) {
                    discountOverlay.style.display = discountOverlay.style.display === 'none' ? 'flex' : 'none';
                  }
                }}
              >
                <span>%</span>
              </button>
              <button 
                className="remove-button" 
                onClick={() => removeProcedure(index)}
              >
                ×
              </button>
            </div>
            <div id={`discount-overlay-${index}`} className="discount-overlay" style={{ display: 'none' }}>
              <div className="discount-overlay-header">Aplicar desconto</div>
              {[10, 15, 20, 25].map((discount) => (
                <button
                  key={discount}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const originalValue = parseCurrencyValue(proc.value);
                    const discountedValue = originalValue * (1 - discount / 100);
                    updateProcedure(index, 'value', parseFloat(discountedValue.toFixed(2)));
                    document.getElementById(`discount-overlay-${index}`).style.display = 'none';
                  }}
                >
                  {discount}%
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    ));
  };

  const filteredProcedures = procedures.filter(proc => 
    proc.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const checkMissingFields = () => {
    const missing = [];
    if (!selectedProfessional) missing.push('Profissional');
    if (!selectedDate) missing.push('Data');
    if (!selectedHour || !selectedMinute) missing.push('Horário');
    if (!selectedDuration) missing.push('Duração');
    if (!selectedPatient || !selectedPatient.pacientId) missing.push('Paciente');
    if (selectedProcedures.length === 0) missing.push('Procedimentos');
    return missing;
  };

  const handleBooking = async () => {
    const missingFieldsList = checkMissingFields();
    
    if (missingFieldsList.length > 0) {
      setMissingFields(missingFieldsList);
      setShowMissingFieldsModal(true);
      setTimeout(() => setShowMissingFieldsModal(false), 3000);
      return;
    }
    
    const formattedTime = `${selectedHour.padStart(2, '0')}:${selectedMinute.padStart(2, '0')}`;
    const bookingData = {
      professionalId: selectedProfessional.id,
      patientId: selectedPatient.pacientId || selectedPatient.patientId,
      dataAtendimento: `${selectedDate}T${formattedTime}:00Z`,
      duration: parseInt(selectedDuration),
      procedimentos: selectedProcedures.map(proc => ({
        nome: proc.subProcedure ? `${proc.procedure} - ${proc.subProcedure}` : proc.procedure,
        valor: parseCurrencyValue(proc.value),
        commission: proc.commission
      })),
      observacao: generalObservation,
      room_id: selectedRoom?.id,
      booking_agent: selectedBookingAgent?.name || null,
      booking_channel: selectedChannel === 'Outro' ? `Outro: ${otherChannelText || ''}` : (selectedChannel || null)
    };

    try {
      setIsLoading(true);
      await createBooking(bookingData);
      setSuccessMessage('Agendamento criado com sucesso!');
      resetForm();
    } catch (error) {
      console.error('Booking creation failed:', error);
      setSuccessMessage(`Erro ao criar agendamento: ${error.message || 'Por favor, tente novamente.'}`);
    } finally {
      setIsLoading(false);
      setTimeout(() => setSuccessMessage(''), 5000);
    }
  };

  const resetForm = () => {
    setSelectedProfessional(null);
    setSelectedDate(null);
    setSelectedHour('');
    setSelectedMinute('');
    setSelectedDuration('30');
    setSelectedPatient(null);
    setSelectedProcedures([]);
    setGeneralObservation('');
    setSearchTerm('');
    setPatientLastBookings([]);
    setSelectedRoom(null);
    setSelectedBookingAgent(null);
    setSelectedChannel('');
    setOtherChannelText('');
    
    const dateInput = dateInputRef.current;
    if (dateInput) {
      dateInput.value = '';
    }

    if (patientSearchRef.current) {
      patientSearchRef.current.resetSearch();
    }
  };

  const MissingFieldsModal = () => {
    if (!showMissingFieldsModal) return null;

    return (
      <div className="modal-overlay" onClick={() => setShowMissingFieldsModal(false)}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <button className="close-button" onClick={() => setShowMissingFieldsModal(false)}>×</button>
          <h3>Campos Obrigatórios</h3>
          <p>Por favor, preencha os campos a seguir para poder agendar:</p>
          <ul>
            {missingFields.map((field, index) => (
              <li key={index}>{field}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  // Function to check if a time slot conflicts with existing bookings
  const checkTimeSlotConflicts = (startTime, duration, bookings) => {
    if (!startTime || !duration || !bookings.length) return false;
    
    // Parse the start time
    const [hours, minutes] = startTime.split(':').map(Number);
    const startMinutes = hours * 60 + minutes;
    const endMinutes = startMinutes + parseInt(duration);
    
    // Check for conflicts with existing bookings
    return bookings.some(booking => {
      if (!booking.datetimeAtendimento) return false;
      
      const bookingTime = booking.datetimeAtendimento.split('T')[1];
      const [bookingHours, bookingMinutes] = bookingTime.slice(0, 5).split(':').map(Number);
      const bookingStartMinutes = bookingHours * 60 + bookingMinutes;
      const bookingEndMinutes = bookingStartMinutes + (booking.duration || 30);
      
      // Check if time slots overlap
      return (
        (startMinutes < bookingEndMinutes && endMinutes > bookingStartMinutes) ||
        (bookingStartMinutes < endMinutes && bookingEndMinutes > startMinutes)
      );
    });
  };

  // Create a simulated booking based on current form selections
  const getSimulatedBooking = () => {
    if (!selectedDate || !selectedHour || !selectedMinute || !selectedDuration || !selectedRoom) {
      return null;
    }
    
    const formattedTime = `${selectedHour.padStart(2, '0')}:${selectedMinute.padStart(2, '0')}`;
    
    return {
      paciente: selectedPatient?.paciente || selectedPatient?.nome || selectedPatient?.Nome || "Agendamento",
      profissional: selectedProfessional?.id || "unknown",
      datetimeAtendimento: `${selectedDate}T${formattedTime}:00Z`,
      duration: parseInt(selectedDuration),
      isSimulated: true
    };
  };

  const simulatedBooking = getSimulatedBooking();
  const hasTimeConflict = simulatedBooking && 
    checkTimeSlotConflicts(
      `${selectedHour.padStart(2, '0')}:${selectedMinute.padStart(2, '0')}`, 
      selectedDuration, 
      roomBookings
    );
  const selectedProcedureNames = selectedProcedures.map(proc => proc.subProcedure ? `${proc.procedure} - ${proc.subProcedure}` : proc.procedure);
  const formattedSelectedTime = selectedHour && selectedMinute
    ? `${selectedHour.padStart(2, '0')}:${selectedMinute.padStart(2, '0')}`
    : '';
  const formattedSelectedDateTime = selectedDate && formattedSelectedTime
    ? `${selectedDate} às ${formattedSelectedTime}`
    : '—';

  return (
    <div className="booking-create-page">
      <div className="booking-page-header">
        <div className="booking-header-icon">
          <FaRegCalendarCheck />
        </div>
        <div>
          <h1>Criar Agendamento</h1>
          <p>Preencha os dados abaixo para agendar uma consulta.</p>
        </div>
      </div>

      <div className="booking-workspace">
        <div className="booking-main-column">
          <section className="booking-panel">
            <h2><FaUser /> Informações principais</h2>
            <div className="booking-form-grid two-columns">
              <div className="booking-field">
                <label>Profissional <span className="required">*</span></label>
                <div className="search-status">
                  <div className="status-multiselect">
                    <button
                      type="button"
                      className="status-trigger"
                      onClick={(e) => {
                        const dropdown = e.currentTarget.nextSibling;
                        if (dropdown) dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                      }}
                    >
                      {selectedProfessional?.name || 'Selecionar profissional'}
                    </button>
                    <div className="status-dropdown" style={{ display: 'none' }}>
                      {professionals.map((prof) => (
                        <div
                          key={prof.id}
                          className="status-option"
                          onClick={(e) => {
                            setSelectedProfessional(prof);
                            const dropdown = e.currentTarget.parentElement;
                            if (dropdown && dropdown.classList.contains('status-dropdown')) {
                              dropdown.style.display = 'none';
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <span>{prof.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="booking-field">
                <label>Paciente <span className="required">*</span></label>
                <PacientSearchSelect 
                  ref={patientSearchRef}
                  onPatientSelect={handlePatientSelect}
                  selectedPatient={selectedPatient}
                />
              </div>

              <div className="booking-field">
                <label>Responsável pelo agendamento (opcional)</label>
                <select
                  className="date-input"
                  value={selectedBookingAgent?.name || ''}
                  onChange={(e) => {
                    const agent = bookingAgents.find(a => a.name === e.target.value);
                    setSelectedBookingAgent(agent || null);
                  }}
                >
                  <option value="">Selecione um responsável</option>
                  {bookingAgents.map(agent => (
                    <option key={agent.name} value={agent.name}>{agent.name}</option>
                  ))}
                </select>
              </div>

              <div className="booking-field">
                <label>Canal do agendamento (opcional)</label>
                <select
                  className="date-input"
                  value={selectedChannel}
                  onChange={(e) => setSelectedChannel(e.target.value)}
                >
                  <option value="">Selecione um canal</option>
                  <option value="Whatsapp">Whatsapp</option>
                  <option value="Telefone">Telefone</option>
                  <option value="Ao vivo">Ao vivo</option>
                  <option value="Instagram">Instagram</option>
                  <option value="Facebook">Facebook</option>
                  <option value="Indicação">Indicação</option>
                  <option value="Site">Site</option>
                  <option value="Outro">Outro</option>
                </select>
                {selectedChannel === 'Outro' && (
                  <input
                    type="text"
                    className="date-input"
                    placeholder="Especifique o canal"
                    value={otherChannelText}
                    onChange={(e) => setOtherChannelText(e.target.value)}
                    style={{ marginTop: '8px' }}
                  />
                )}
              </div>
            </div>

            {selectedPatient && patientLastBookings.length > 0 && (
              <div className="patient-last-bookings">
                <h4>Últimos atendimentos</h4>
                <div className="last-bookings-list">
                  {patientBookingsLoading ? (
                    <div className="loading-indicator">Carregando...</div>
                  ) : (
                    patientLastBookings.map((booking, index) => (
                      <div key={index} className="last-booking-item">
                        {booking.procedimentos.map((proc, i) => (
                          <span key={i} className="last-booking-procedure">
                            {proc.nome}
                          </span>
                        ))}
                        <span className="last-booking-date">
                          {new Date(booking.datetimeAtendimento).toLocaleDateString()}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </section>

          <section className="booking-panel">
            <h2><FaClipboardList /> Procedimentos</h2>
            <div className="procedure-search">
              <input
                type="text"
                placeholder="Buscar procedimentos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="procedure-search-input"
              />
            </div>
            
            {!showSubProcedures ? (
              <div className="procedure-list">
                {filteredProcedures.map((proc) => (
                  <div 
                    key={proc.name}
                    className="procedure-item"
                    onClick={() => handleProcedureSelect(proc)}
                  >
                    {proc.name}
                  </div>
                ))}
              </div>
            ) : (
              <div className="subprocedure-list">
                <div className="subprocedure-header">
                  <button onClick={() => setShowSubProcedures(false)}>Voltar</button>
                  <h4>{currentProcedure.name}</h4>
                </div>
                {Object.entries(currentProcedure.subcategorias).map(([key, value]) => (
                  <div 
                    key={key}
                    className="subprocedure-item"
                    onClick={() => handleSubProcedureSelect(key, value)}
                  >
                    {key} - R$ {value.valor}
                  </div>
                ))}
              </div>
            )}

            <div className="selected-procedures">
              <h4>Procedimentos selecionados</h4>
              {renderSelectedProcedures()}
            </div>
          </section>

          <section className="booking-panel">
            <h2><FaNotesMedical /> Detalhes adicionais</h2>
            <div className="booking-form-grid two-columns">
              <div className="booking-field">
                <label>Sala</label>
                <div className="search-status">
                  <div className="status-multiselect">
                    <button
                      type="button"
                      className="status-trigger"
                      onClick={(e) => {
                        const dropdown = e.currentTarget.nextSibling;
                        if (dropdown) dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                      }}
                    >
                      {selectedRoom?.name || 'Selecionar sala'}
                    </button>
                    <div className="status-dropdown" style={{ display: 'none' }}>
                      {rooms.map((room) => (
                        <div
                          key={room.id}
                          className="status-option"
                          onClick={(e) => {
                            setSelectedRoom(room);
                            const dropdown = e.currentTarget.parentElement;
                            if (dropdown && dropdown.classList.contains('status-dropdown')) {
                              dropdown.style.display = 'none';
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <span>{room.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="booking-field">
                <label>Observação (opcional)</label>
                <textarea 
                  className="date-input"
                  value={generalObservation}
                  onChange={(e) => setGeneralObservation(e.target.value)}
                  placeholder="Observações gerais sobre o agendamento..."
                  rows="5"
                />
              </div>
            </div>

            {selectedRoom && selectedDate && selectedProfessional && (
              <div className="room-bookings">
                <h4>Agendamentos para {selectedRoom.name} em {selectedDate}</h4>
                {roomBookingsLoading ? (
                  <div className="loading-indicator">Carregando agendamentos...</div>
                ) : (
                  <>
                    <div className="room-time-slots">
                      <div className="time-slots-header">
                        <div className="time-slot-label">Horário</div>
                        <div className="time-slot-content">Agendamento</div>
                      </div>
                      <div className="time-slots-container">
                        {Array.from({length: 16}, (_, i) => i + 7).map(hour => {
                          const hourStr = hour.toString().padStart(2, '0');
                          const timeSlots = [0, 15, 30, 45].map(minute => {
                            const minuteStr = minute.toString().padStart(2, '0');
                            const timeStr = `${hourStr}:${minuteStr}`;
                            const existingBookings = roomBookings.filter(booking => {
                              if (!booking) return false;
                              const bookingTime = booking.datetimeAtendimento.split('T')[1];
                              const [bookingHours, bookingMinutes] = bookingTime.slice(0, 5).split(':').map(Number);
                              const bookingStartMinutes = bookingHours * 60 + bookingMinutes;
                              const bookingEndMinutes = bookingStartMinutes + (booking.duration || 30);
                              const slotMinutes = hour * 60 + minute;
                              return slotMinutes >= bookingStartMinutes && slotMinutes < bookingEndMinutes;
                            });
                            let hasSimulated = false;
                            if (simulatedBooking) {
                              const bookingTime = simulatedBooking.datetimeAtendimento.split('T')[1];
                              const [bookingHours, bookingMinutes] = bookingTime.slice(0, 5).split(':').map(Number);
                              const bookingStartMinutes = bookingHours * 60 + bookingMinutes;
                              const bookingEndMinutes = bookingStartMinutes + (simulatedBooking.duration || 30);
                              const slotMinutes = hour * 60 + minute;
                              hasSimulated = slotMinutes >= bookingStartMinutes && slotMinutes < bookingEndMinutes;
                            }
                            const hasConflict = existingBookings.length > 0 && hasSimulated;
                            let timeSlotClass = 'time-slot';
                            if (hasConflict) {
                              timeSlotClass += ' conflict';
                            } else if (hasSimulated) {
                              timeSlotClass += ' simulated';
                            } else if (existingBookings.length > 0) {
                              timeSlotClass += ' occupied';
                            }
                            if (selectedHour === hourStr && selectedMinute === minuteStr) {
                              timeSlotClass += ' selected';
                            }
                            return (
                              <div key={timeStr} className={timeSlotClass}>
                                <div className="time-slot-label">{timeStr}</div>
                                <div className="time-slot-content">
                                  {existingBookings.map((booking, idx) => (
                                    <div 
                                      key={idx} 
                                      className={`slot-booking existing ${booking.status === 'Bloqueio' ? 'blocking' : ''}`}
                                      title={booking.status === 'Bloqueio' 
                                        ? `Bloqueio - ${professionals.find(p => p.id === booking.profissional)?.name || 'Profissional'}${booking.observacao ? ': ' + booking.observacao : ''}`
                                        : `${booking.paciente} - ${professionals.find(p => p.id === booking.profissional)?.name || 'Profissional'}`}
                                    >
                                      {booking.status === 'Bloqueio' ? 'Bloqueio' : booking.paciente}
                                    </div>
                                  ))}
                                  {hasSimulated && (
                                    <div 
                                      className={`slot-booking simulated ${hasConflict ? 'conflict' : ''}`}
                                      title={`${selectedPatient?.paciente || 'Paciente'} - ${selectedProfessional?.name || 'Profissional'}`}
                                    >
                                      {hasConflict ? 'Conflito!' : 'Agendando agora'}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          });
                          return (
                            <div key={hour} className="hour-slots">
                              {timeSlots}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {hasTimeConflict && (
                      <div className="conflict-warning">
                        <p>O horário selecionado conflita com outro agendamento nesta sala. Escolha outro horário ou sala.</p>
                      </div>
                    )}
                    {roomBookings.length === 0 && !hasTimeConflict && (
                      <p>Nenhum agendamento para esta sala na data selecionada.</p>
                    )}
                  </>
                )}
              </div>
            )}
            
            {selectedRoom && (!selectedDate || !selectedHour || !selectedMinute || !selectedDuration || !selectedProfessional) && (
              <div className="room-booking-message">
                <p>Escolha o profissional, data e horário para verificar se há conflitos na sala</p>
              </div>
            )}
          </section>
        </div>

        <aside className="booking-side-column">
          <section className="booking-panel booking-sticky-panel">
            <h2><FaCalendarAlt /> Data e horário</h2>
            <div className="booking-field">
              <label>Selecione a data</label>
              <div 
                className="date-input-container"
                onClick={() => {
                  if (dateInputRef.current && typeof dateInputRef.current.showPicker === 'function') {
                    dateInputRef.current.showPicker();
                  } else if (dateInputRef.current) {
                    dateInputRef.current.focus();
                    dateInputRef.current.click();
                  }
                }}
              >
                <input 
                  type="date" 
                  className="date-input"
                  ref={dateInputRef}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </div>
            </div>

            <div className="booking-field">
              <label>Selecione o horário</label>
              <div className="time-selection-container">
                <div className="time-column">
                  <h4>Hora</h4>
                  <div className="time-scroll">
                    {[...Array(15)].map((_, i) => (
                      <div 
                        key={i} 
                        className={`time-item ${selectedHour === (i + 8).toString() ? 'selected' : ''}`}
                        onClick={() => setSelectedHour((i + 8).toString())}
                      >
                        {(i + 8).toString().padStart(2, '0')}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="time-column">
                  <h4>Minuto</h4>
                  <div className="time-scroll">
                    {[...Array(12)].map((_, i) => (
                      <div 
                        key={i} 
                        className={`time-item ${selectedMinute === (i * 5).toString() ? 'selected' : ''}`}
                        onClick={() => setSelectedMinute((i * 5).toString())}
                      >
                        {(i * 5).toString().padStart(2, '0')}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="duration-row">
              <label>Duração</label>
              <input 
                type="text"
                inputMode="decimal"
                className="date-input"
                value={selectedDuration}
                onChange={(e) => setSelectedDuration(e.target.value.replace(',', '.'))}
                placeholder="Tempo de duração"
              />
            </div>
          </section>

          <section className="booking-panel booking-summary-panel">
            <h2><FaClipboardList /> Resumo do agendamento</h2>
            <div className="booking-summary-list">
              <div>
                <span>Procedimento</span>
                <strong>{selectedProcedureNames.length > 0 ? selectedProcedureNames.join(', ') : '—'}</strong>
              </div>
              <div>
                <span>Paciente</span>
                <strong>{selectedPatient?.Nome || selectedPatient?.paciente || selectedPatient?.nome || '—'}</strong>
              </div>
              <div>
                <span>Profissional</span>
                <strong>{selectedProfessional?.name || '—'}</strong>
              </div>
              <div>
                <span>Sala</span>
                <strong>{selectedRoom?.name || '—'}</strong>
              </div>
              <div>
                <span>Duração</span>
                <strong>{selectedDuration ? `${selectedDuration} min` : '—'}</strong>
              </div>
              <div>
                <span>Data e horário</span>
                <strong>{formattedSelectedDateTime}</strong>
              </div>
            </div>

            <button 
              className="book-button"
              onClick={handleBooking}
              disabled={isLoading}
            >
              {isLoading ? <ClipLoader size={20} color={"#ffffff"} /> : <><FaPlus /> Confirmar Agendamento</>}
            </button>
            <div className="booking-confirm-note">
              <FaLock />
              <span>Seu agendamento será confirmado após revisão dos dados.</span>
            </div>
          </section>
        </aside>
      </div>

      {successMessage && (
        <div className={`success-message ${successMessage.includes('erro') ? 'error' : 'success'}`}>
          {successMessage}
        </div>
      )}
      
      <MissingFieldsModal />
    </div>
  );
}

export default CreateBooking; 
